import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type MutableRefObject, type ReactElement } from 'react';
import { keepPreviousData, useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Icon } from '@/ui/Icon';
import { Callout, Chip, Loading, Switch } from '@/ui/bits';
import { MenuHead, MenuItem, Popover, usePopover } from '@/ui/Popover';
import { ApiError, errorMessage } from '@/lib/api';
import type { ComposeRequest } from '@/lib/actions';
import { useMe } from '@/lib/hooks';
import { toast, toastError } from '@/lib/toast';
import { openAccount } from '@/shell/AccountDialog';
import type { Me } from '@shared/types';
import {
  fillMergeFields, firstName, hasMergeFields,
  type ComposeInfo, type ComposeInput, type ComposeMailbox, type ComposeTarget, type OutboxItem, type ReplyContext, type Template,
} from '@shared/mail';
import { AttachmentList, fromSaved, toSaved, useAttachments } from './Attachments';
import { cancelOutbox, checkRecipients, composeInfoQuery, fetchOutbox, fetchReplyContext, fetchTarget, keys, sendEmail, templatesQuery, timeZone } from './data';
import { Editor, FormatBar, MOD, type EditorHandle, type FormatBarHandle } from './Editor';
import { cleanPastedHtml, toEditorHtml } from './html';
import {
  composerTitle, draftIsEmpty, draftKey, isBlankHtml, scheduleProblem, starterBody, textToEditorHtml, whenPhrase, whoLabel, withoutTitle,
  type ComposeBase, type Draft, type Rcpt,
} from './logic';
import { Recipients, type RecipientsHandle } from './Recipients';
import { SendSplit } from './Schedule';
import { MergeMenu, TemplateRow } from './Templates';
import { closeComposer, dropDraft, loadDraft, minimiseComposer, openComposer, saveDraft, type ComposerSnapshot } from './state';

// The composer: loads what the email starts from (a conversation, a record, a scheduled email), then the form.

/** What the host (the layer around the composer) can ask of the email inside it. */
export interface ComposerControl {
  /** Closes and keeps the draft. */
  close: () => void;
  send: () => void;
  /** Puts focus where writing continues. */
  focus: () => void;
}

interface Start {
  base: ComposeBase;
  values: Draft;
  /** The content came from a saved draft or from Undo. */
  restored: boolean;
}

interface Props {
  req: ComposeRequest;
  snapshot: ComposerSnapshot | null;
  ctl: MutableRefObject<ComposerControl>;
  titleId: string;
  open: boolean;
}

const EDITABLE: OutboxItem['status'][] = ['scheduled', 'failed'];

export function Composer({ req, snapshot, ctl, titleId, open }: Props) {
  const me = useMe();
  const info = useQuery(composeInfoQuery);
  const replyMode = req.mode && req.mode !== 'new' ? req.mode : 'reply';
  const source = snapshot ? 'none' : req.outboxId ? 'outbox' : req.threadId ? 'reply' : req.personId || req.companyId || req.dealId ? 'target' : 'none';

  const outbox = useQuery({ queryKey: keys.outbox(req.outboxId ?? ''), queryFn: () => fetchOutbox(req.outboxId!), enabled: source === 'outbox' });
  const reply = useQuery({
    queryKey: keys.reply(req.threadId ?? '', req.emailId, replyMode),
    queryFn: () => fetchReplyContext(req.threadId!, req.emailId, replyMode),
    enabled: source === 'reply',
  });
  const target = useQuery({
    queryKey: keys.target(req.personId, req.companyId, req.dealId),
    queryFn: () => fetchTarget(req.personId, req.companyId, req.dealId),
    enabled: source === 'target',
  });
  const wantsTemplate = !!req.templateId && !snapshot;
  const templates = useQuery({ ...templatesQuery, enabled: wantsTemplate });

  const needed: UseQueryResult<unknown>[] = [info];
  if (source === 'outbox') needed.push(outbox);
  if (source === 'reply') needed.push(reply);
  if (source === 'target') needed.push(target);
  const failed = needed.find((q) => q.data === undefined && q.isError);
  // A template that does not load leaves the email blank rather than holding it up.
  const ready = needed.every((q) => q.data !== undefined) && (!wantsTemplate || templates.data !== undefined || templates.isError);

  const [start, setStart] = useState<Start | null>(null);
  const gone = outbox.data && (!EDITABLE.includes(outbox.data.status) || !outbox.data.mine);
  if (!start && ready && !gone) {
    setStart(
      buildStart(req, me, info.data!, snapshot, {
        outbox: source === 'outbox' ? outbox.data : undefined,
        reply: source === 'reply' ? reply.data : undefined,
        target: source === 'target' ? target.data : undefined,
        template: wantsTemplate ? templates.data?.find((t) => t.id === req.templateId) : undefined,
      }),
    );
  }

  const fallbackTitle = req.outboxId ? 'Edit scheduled email' : composerTitle(req.threadId ? replyMode : 'new');
  if (start && info.data) return <ComposerForm req={req} info={info.data} start={start} ctl={ctl} titleId={titleId} open={open} />;

  const shell = (body: ReactElement) => (
    <>
      <div className="cmp-h">
        <h2 id={titleId}>{fallbackTitle}</h2>
        <button type="button" className="icon-btn sm" aria-label="Close composer" data-tip="Close" data-key="Esc" onClick={closeComposer}>
          <Icon name="x" />
        </button>
      </div>
      <div className="cmp-state">{body}</div>
    </>
  );
  ctl.current = { close: closeComposer, send: () => undefined, focus: () => undefined };

  if (gone) {
    return shell(
      <Callout tone="info" title="This email can no longer be changed" role="status" actions={<CloseButton />}>
        {outbox.data!.status === 'cancelled' ? 'It was cancelled.' : outbox.data!.mine ? 'It already went out.' : 'Only the person who wrote it can change it.'}
      </Callout>,
    );
  }
  if (failed && failed.error instanceof ApiError && failed.error.status === 404) {
    return shell(
      <Callout tone="info" title={source === 'outbox' ? 'This email can no longer be changed' : source === 'reply' ? 'This conversation is not available' : 'This record is not available'} role="status" actions={<CloseButton />}>
        {errorMessage(failed.error)}
      </Callout>,
    );
  }
  if (failed) {
    return shell(
      <Callout
        tone="danger"
        title="The email is not loading"
        role="alert"
        actions={
          <>
            <button type="button" className="btn btn-sm btn-tile" onClick={() => needed.forEach((q) => q.isError && void q.refetch())}>
              <Icon name="refresh" />
              Try again
            </button>
            <CloseButton />
          </>
        }
      >
        {errorMessage(failed.error)}
      </Callout>,
    );
  }
  return shell(<Loading label={source === 'reply' ? 'Opening the conversation' : 'Opening the composer'} />);
}

function CloseButton() {
  return (
    <button type="button" className="btn btn-sm btn-ghost" onClick={closeComposer}>
      Close
    </button>
  );
}

function rcpt(r: { email: string; name: string | null; personId?: string | null }): Rcpt {
  return { email: r.email, name: r.name, personId: r.personId ?? null };
}

/** Works out what the composer starts with: Undo's snapshot, else a saved draft, else the email it was opened for. */
function buildStart(
  req: ComposeRequest,
  me: Me,
  info: ComposeInfo,
  snapshot: ComposerSnapshot | null,
  data: { outbox?: OutboxItem; reply?: ReplyContext; target?: ComposeTarget; template?: Template },
): Start {
  const usable = (id: string | null | undefined) => (id && info.mailboxes.some((m) => m.id === id) ? id : info.defaultMailboxId);
  if (snapshot) {
    const { base, ...values } = snapshot;
    return { base, values: { ...values, mailboxId: usable(values.mailboxId) }, restored: true };
  }
  const myFirst = firstName(me.user.name);
  const blank: Draft = {
    mailboxId: info.defaultMailboxId, to: [], cc: [], bcc: [], subject: '', html: '', attachments: [], templateId: null, trackOpens: true, sendAt: null, savedAt: 0,
  };
  let base: ComposeBase;
  let values: Draft;
  const o = data.outbox;
  const c = data.reply;
  if (o) {
    const html = toEditorHtml(o.bodyHtml);
    const later = o.status === 'scheduled' && new Date(o.sendAt).getTime() > Date.now() + 60_000;
    base = {
      mode: o.forwardOf ? 'forward' : o.replyToEmailId ? 'reply' : 'new', editing: true, name: o.to[0]?.name ?? o.person?.name ?? null, quote: null,
      replyToEmailId: o.replyToEmailId, forwardOfEmailId: o.forwardOf?.id ?? null, threadId: o.threadId, personId: o.person?.id ?? null,
      companyId: o.company?.id ?? null, dealId: o.deal?.id ?? null, outboxId: o.id, starter: html,
    };
    values = {
      ...blank, mailboxId: usable(o.mailbox?.id), to: o.to.map(rcpt), cc: o.cc.map(rcpt), bcc: o.bcc.map(rcpt), subject: o.subject, html,
      attachments: o.attachments.map((a) => ({ ...a })), templateId: o.templateId, trackOpens: o.trackOpens, sendAt: later ? o.sendAt : null,
    };
  } else if (c) {
    const one = c.to.length === 1 ? c.to[0]! : null;
    const starter = starterBody(c.mode === 'forward' || !one ? null : firstName(one.name, one.email), myFirst);
    base = {
      mode: c.mode, editing: false, name: c.mode === 'reply' ? (one?.name ?? one?.email ?? null) : null, quote: c.quote,
      replyToEmailId: c.replyToEmailId, forwardOfEmailId: c.mode === 'forward' ? c.parent.id : null, threadId: c.threadId,
      personId: c.personId, companyId: c.companyId, dealId: c.dealId, outboxId: null, starter,
    };
    values = {
      ...blank, mailboxId: usable(c.mailboxId), to: c.to.map(rcpt), cc: c.cc.map(rcpt), subject: c.subject, html: starter,
      attachments: c.attachments.map((a) => ({ ...a })), trackOpens: c.trackOpens,
    };
  } else {
    const t = data.target;
    const to: Rcpt[] = t ? t.to.map((s) => rcpt(s)) : (req.to ?? []).map((r) => rcpt({ email: r.email, name: r.name ?? null, personId: r.personId }));
    const one = to.length === 1 ? to[0]! : null;
    const starter = starterBody(one ? firstName(one.name, one.email) : null, myFirst);
    base = {
      mode: 'new', editing: false, name: t?.name ?? one?.name ?? null, quote: null, replyToEmailId: null, forwardOfEmailId: null, threadId: null,
      personId: t?.personId ?? req.personId ?? null, companyId: t?.companyId ?? req.companyId ?? null, dealId: t?.dealId ?? req.dealId ?? null,
      outboxId: null, starter,
    };
    values = { ...blank, to, html: starter };
  }
  if (!o) {
    if (req.subject) values.subject = req.subject;
    if (req.body) values.html = base.starter = req.body.trimStart().startsWith('<') ? toEditorHtml(req.body) : textToEditorHtml(req.body);
    const tpl = data.template;
    if (tpl) {
      const one = values.to.length === 1 ? values.to[0]! : null;
      const company = data.target?.to.find((s) => one && s.email === one.email)?.company ?? null;
      const fill = one ? { first: firstName(one.name, one.email), company, myFirst } : null;
      values.templateId = tpl.id;
      values.html = base.starter = toEditorHtml(fill ? fillMergeFields(tpl.body, fill, 'html') : tpl.body);
      if (base.mode === 'new' && !values.subject) values.subject = fill ? fillMergeFields(tpl.subject, fill) : tpl.subject;
    }
  }
  const draft = loadDraft(me.user.id, draftKey(req));
  if (draft && !draftIsEmpty(draft, base.starter)) return { base, values: { ...draft, mailboxId: usable(draft.mailboxId) }, restored: true };
  return { base, values, restored: false };
}

/* ---------- the form ---------- */

type FieldName = 'from' | 'to' | 'cc' | 'bcc' | 'subject' | 'body' | 'attachments' | 'sendAt';

function mailboxChip(mb: ComposeMailbox) {
  if (mb.kind === 'shared') return <Chip icon="users">Shared</Chip>;
  if (mb.kind === 'personal') return <Chip icon="mail">Gmail</Chip>;
  return <Chip icon="mail">Clientbook</Chip>;
}

function ComposerForm({ req, info, start, ctl, titleId, open }: { req: ComposeRequest; info: ComposeInfo; start: Start; ctl: MutableRefObject<ComposerControl>; titleId: string; open: boolean }) {
  const me = useMe();
  const userId = me.user.id;
  const myFirst = firstName(me.user.name);
  const key = useMemo(() => draftKey(req), [req]);
  const { base, values: init } = start;

  const [mailboxId, setMailboxId] = useState(init.mailboxId);
  const [to, setTo] = useState<Rcpt[]>(init.to);
  const [cc, setCc] = useState<Rcpt[]>(init.cc);
  const [bcc, setBcc] = useState<Rcpt[]>(init.bcc);
  const [showCc, setShowCc] = useState(init.cc.length > 0);
  const [showBcc, setShowBcc] = useState(init.bcc.length > 0);
  const [subject, setSubject] = useState(init.subject);
  const html = useRef(init.html);
  const [templateId, setTemplateId] = useState(init.templateId);
  const [trackOpens, setTrackOpens] = useState(init.trackOpens);
  const [sendAt, setSendAt] = useState<Date | null>(init.sendAt ? new Date(init.sendAt) : null);
  const [version, setVersion] = useState(0);
  const touch = () => setVersion((v) => v + 1);
  const att = useAttachments(useMemo(() => fromSaved(init.attachments), [init.attachments]), info.maxAttachmentBytes, touch);
  const [saved, setSaved] = useState<'new' | 'saving' | 'saved'>(start.restored ? 'saved' : 'new');
  const [invalid, setInvalid] = useState<Partial<Record<FieldName, string>>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [shake, setShake] = useState(0);
  const [attachProblem, setAttachProblem] = useState<string | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(base.mode === 'forward');
  const [dragging, setDragging] = useState(false);

  const toRef = useRef<RecipientsHandle>(null);
  const ccRef = useRef<RecipientsHandle>(null);
  const bccRef = useRef<RecipientsHandle>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const editor = useRef<EditorHandle>(null);
  const fmt = useRef<FormatBarHandle>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  /** Set once the email is sent or discarded, so closing does not keep a draft. */
  const done = useRef(false);

  const mb = info.mailboxes.find((m) => m.id === mailboxId) ?? null;

  /* who the email goes to: names, colours and anyone Clientbook refuses to email */
  const emails = useMemo(() => [...new Set([...to, ...cc, ...bcc].map((r) => r.email.toLowerCase()))].sort(), [to, cc, bcc]);
  const checked = useQuery({
    queryKey: keys.recipients(emails),
    queryFn: () => checkRecipients(emails),
    enabled: emails.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  const known = useMemo(() => new Map((checked.data ?? []).map((s) => [s.email.toLowerCase(), s])), [checked.data]);
  const problems = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of emails) {
      const p = known.get(e)?.problem;
      if (p) m.set(e, p);
    }
    return m;
  }, [known, emails]);
  const hues = useMemo(() => new Map([...known].map(([e, s]) => [e, s.hue])), [known]);
  const refused = [...to, ...cc, ...bcc].filter((r) => problems.has(r.email.toLowerCase()));

  const single = to.length === 1 ? to[0]! : null;
  const mergeValues = useMemo(() => {
    if (!single) return null;
    const k = known.get(single.email.toLowerCase());
    return { first: firstName(k && k.personId ? k.name : single.name, single.email), company: k?.company ?? null, myFirst };
  }, [single, known, myFirst]);

  /* drafts */
  const current = (lists = { to, cc, bcc }): Draft => ({
    mailboxId, ...lists, subject, html: html.current, attachments: toSaved(att.items), templateId, trackOpens, sendAt: sendAt?.toISOString() ?? null, savedAt: Date.now(),
  });
  const latest = useRef(current);
  latest.current = current;

  const persist = () => {
    const d = latest.current();
    if (draftIsEmpty(d, base.starter)) dropDraft(userId, key);
    else saveDraft(userId, key, d);
  };

  useEffect(() => {
    if (!version) return;
    setSaved('saving');
    const t = setTimeout(() => {
      if (done.current) return;
      persist();
      setSaved('saved');
    }, 600);
    return () => clearTimeout(t);
    // Only a change to the email saves it.
  }, [version]);

  const versionRef = useRef(version);
  versionRef.current = version;
  // Replaced by another email or the page closing: keep what was written.
  useEffect(() => {
    const flush = () => {
      if (!done.current && versionRef.current > 0) persist();
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);

  const close = () => {
    if (done.current) return closeComposer();
    const d = latest.current();
    done.current = true;
    if (draftIsEmpty(d, base.starter)) {
      dropDraft(userId, key);
      closeComposer();
      return;
    }
    closeComposer();
    if (version > 0 || start.restored) {
      saveDraft(userId, key, d);
      toast('Draft saved', { icon: 'edit', action: { label: 'Open', run: () => openComposer(req) } });
    }
  };

  const discard = () => {
    const d = latest.current();
    done.current = true;
    dropDraft(userId, key);
    closeComposer();
    toast('Draft discarded', {
      icon: 'trash',
      undo: () => {
        saveDraft(userId, key, d);
        openComposer(req, { ...d, base });
      },
    });
  };

  /* focus */
  const focusStart = () => {
    if (to.length + cc.length + bcc.length === 0) toRef.current?.focus();
    else if (!subject.trim()) subjectRef.current?.focus();
    else editor.current?.focus(start.restored ? 'end' : 'afterGreeting');
  };
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(focusStart, 60);
    return () => clearTimeout(t);
    // Only when the form first appears; coming back from the dock restores focus where it was.
  }, []);

  /* fields */
  const clearField = (f: FieldName) => {
    if (invalid[f]) setInvalid(({ [f]: _gone, ...rest }) => rest);
    setNote(null);
    setServerError(null);
  };
  const flag = (f: FieldName, message: string) => {
    setInvalid({ [f]: message });
    setNote(message);
    setShake((n) => n + 1);
  };

  const addFiles = (files: File[]) => {
    const problem = att.add(files);
    setAttachProblem(problem);
    if (!problem) clearField('attachments');
  };

  const applyTemplate = (t: Template | null) => {
    const prev = { templateId, html: html.current, subject };
    const undo = () => {
      editor.current?.setHtml(prev.html);
      setSubject(prev.subject);
      setTemplateId(prev.templateId);
      touch();
    };
    if (!t) {
      setTemplateId(null);
      touch();
      toast('Template removed, your text stays', { icon: 'layers', undo });
      return;
    }
    editor.current?.setHtml(toEditorHtml(mergeValues ? fillMergeFields(t.body, mergeValues, 'html') : t.body));
    setTemplateId(t.id);
    if (base.mode === 'new') setSubject(mergeValues ? fillMergeFields(t.subject, mergeValues) : t.subject);
    clearField('body');
    touch();
    toast(
      <>
        Template <b>{t.name}</b> applied
      </>,
      { icon: 'layers', undo },
    );
  };

  /* sending */
  const send = useMutation({ mutationFn: sendEmail });

  const submit = () => {
    if (send.isPending) return;
    setServerError(null);
    setNote(null);
    const lists = { to: [...to], cc: [...cc], bcc: [...bcc] };
    const rows = [
      ['to', toRef],
      ['cc', ccRef],
      ['bcc', bccRef],
    ] as const;
    for (const [name, ref] of rows) {
      const r = ref.current?.commit();
      if (!r) continue;
      lists[name].push(...r.added);
      if (r.left.trim()) {
        flag(name, `Check the address ${r.left.trim()}`);
        ref.current?.focus();
        return;
      }
    }
    if (lists.to.length + lists.cc.length + lists.bcc.length === 0) {
      flag('to', 'Add who this goes to first');
      toRef.current?.focus();
      return;
    }
    if (lists.to.length === 0) {
      flag('to', 'Put at least one person in To');
      toRef.current?.focus();
      return;
    }
    if (!mb) {
      flag('from', 'Connect your own Gmail to send');
      return;
    }
    if (!mb.canSend) {
      flag('from', mb.problem ?? 'This inbox cannot send');
      return;
    }
    const blocked = [...lists.to, ...lists.cc, ...lists.bcc].find((r) => problems.has(r.email.toLowerCase()));
    if (blocked) {
      flag('to', problems.get(blocked.email.toLowerCase())!);
      return;
    }
    if (!subject.trim()) {
      flag('subject', 'Add a subject');
      subjectRef.current?.focus();
      return;
    }
    if (base.mode !== 'forward' && isBlankHtml(html.current)) {
      flag('body', 'Write a message');
      editor.current?.focus('start');
      return;
    }
    if (att.uploading) {
      flag('attachments', 'Wait for the files to finish uploading');
      return;
    }
    if (att.failed) {
      flag('attachments', 'Remove the files that did not upload');
      return;
    }
    const late = sendAt ? scheduleProblem(sendAt) : null;
    if (late) {
      flag('sendAt', late);
      return;
    }

    const strip = (r: Rcpt) => ({ email: r.email, name: r.name, personId: r.personId ?? null });
    const body = base.quote ? `${html.current}<div><br></div>${base.quote.html}` : html.current;
    const input: ComposeInput = {
      mailboxId: mb.id, to: lists.to.map(strip), cc: lists.cc.map(strip), bcc: lists.bcc.map(strip), subject: subject.trim(), html: body, text: '',
      attachments: att.items.flatMap((a) => (a.ref ? [a.ref] : [])), templateId, trackOpens, sendAt: sendAt?.toISOString() ?? null,
      replyToEmailId: base.replyToEmailId, forwardOfEmailId: base.forwardOfEmailId, threadId: base.threadId, personId: base.personId,
      companyId: base.companyId, dealId: base.dealId, outboxId: base.outboxId, timeZone: timeZone(),
    };
    // Undo brings back this email as a new one: the scheduled email it replaced is cancelled by now.
    const snapshot: ComposerSnapshot = { ...current(lists), base: { ...base, editing: false, outboxId: null } };
    const again: ComposeRequest = { ...req, outboxId: undefined };
    const tracking = trackOpens;

    send.mutate(input, {
      onSuccess: (r) => {
        done.current = true;
        dropDraft(userId, key);
        closeComposer();
        const who = whoLabel(lists.to);
        const undo = () => {
          cancelOutbox(r.outboxIds).then(
            () => {
              openComposer(again, snapshot);
              toast(r.scheduled ? 'Schedule cancelled. Your draft is back' : 'Unsent. Your draft is back', { icon: 'undo' });
            },
            (err: unknown) => toastError(err),
          );
        };
        if (r.scheduled) {
          toast(
            <>
              {base.editing ? 'Changes saved. ' : ''}Scheduled for <b>{whenPhrase(new Date(r.sendAt))}</b> · {who} {lists.to.length === 1 ? 'gets' : 'get'} it then
            </>,
            { icon: 'clock', undo },
          );
        } else {
          toast(`Sent to ${who}${r.personal ? ', one copy each' : ''}${tracking ? ' · open tracking on' : ''}`, { icon: 'send', undo });
        }
      },
      onError: (err) => {
        const fields = err instanceof ApiError ? (err.fields as Partial<Record<FieldName, string>>) : {};
        setInvalid(fields);
        setServerError(errorMessage(err));
        if (err instanceof ApiError && err.details.problems) void checked.refetch();
        if (fields.subject) subjectRef.current?.focus();
        else if (fields.body) editor.current?.focus('start');
        else if (fields.to) toRef.current?.focus();
      },
    });
  };

  ctl.current = { close, send: submit, focus: focusStart };

  /* the note beside Send */
  const fromName = mb?.kind === 'personal' ? 'Gmail' : mb?.address;
  let noteEl: ReactElement | null = null;
  if (note) {
    noteEl = (
      <span id="cmpSchedNote" className="err" role="alert">
        <Icon name="warn" className="i-xs" />
        {note}
      </span>
    );
  } else if (sendAt) {
    noteEl = (
      <span id="cmpSchedNote" className="on">
        <Icon name="clock" className="i-xs" />
        Sends {whenPhrase(sendAt)}
      </span>
    );
  } else if (fromName) {
    noteEl = <span id="cmpSchedNote">Sends now from {fromName}</span>;
  }

  const savedLabel = saved === 'saving' ? 'Saving draft' : saved === 'saved' ? 'Draft saved' : base.editing ? 'Scheduled' : 'New draft';
  const otherSender = info.mailboxes.some((m) => m.canSend && m.id !== mailboxId);
  const merged = hasMergeFields(`${subject}\n${html.current}`);
  const typed = () => {
    setNote(null);
    setServerError(null);
  };

  const onDragOver = (e: DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e: DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
  };
  const onDrop = (e: DragEvent) => {
    if (!e.dataTransfer.files.length) return;
    e.preventDefault();
    setDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div className="cmp-in" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <div className="cmp-h">
        <h2 id={titleId}>{composerTitle(base.mode, { name: base.name, editing: base.editing })}</h2>
        <span className="hint" aria-live="polite">
          {saved === 'saved' ? <Icon name="check-circle" /> : null}
          <span>{savedLabel}</span>
        </span>
        <button type="button" className="icon-btn sm" aria-label="Minimise" data-tip="Minimise" data-key={`${MOD} .`} onClick={minimiseComposer}>
          <Icon name="minus" />
        </button>
        <button type="button" className="icon-btn sm" aria-label="Close composer" data-tip="Close" data-key="Esc" onClick={close}>
          <Icon name="x" />
        </button>
      </div>

      <div className="cmp-rows">
        <FromRow
          mailboxes={info.mailboxes}
          value={mailboxId}
          invalid={!!invalid.from}
          onChange={(id) => {
            setMailboxId(id);
            clearField('from');
            touch();
          }}
        />
        <Recipients
          ref={toRef}
          label="To"
          list={to}
          onChange={(l) => {
            setTo(l);
            clearField('to');
            touch();
          }}
          problems={problems}
          taken={[...cc, ...bcc].map((r) => r.email)}
          hues={hues}
          invalid={!!invalid.to}
          shake={!!invalid.to && shake % 2 === 1}
          onTyped={typed}
          aside={
            !showCc || !showBcc ? (
              <span className="cmp-ccbtns">
                {!showCc ? (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowCc(true)} aria-label="Add Cc">
                    Cc
                  </button>
                ) : null}
                {!showBcc ? (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowBcc(true)} aria-label="Add Bcc">
                    Bcc
                  </button>
                ) : null}
              </span>
            ) : null
          }
        />
        {showCc ? (
          <Recipients
            ref={ccRef}
            label="Cc"
            list={cc}
            onChange={(l) => {
              setCc(l);
              clearField('cc');
              touch();
            }}
            problems={problems}
            taken={[...to, ...bcc].map((r) => r.email)}
            hues={hues}
            invalid={!!invalid.cc}
            onTyped={typed}
          />
        ) : null}
        {showBcc ? (
          <Recipients
            ref={bccRef}
            label="Bcc"
            list={bcc}
            onChange={(l) => {
              setBcc(l);
              clearField('bcc');
              touch();
            }}
            problems={problems}
            taken={[...to, ...cc].map((r) => r.email)}
            hues={hues}
            invalid={!!invalid.bcc}
            onTyped={typed}
          />
        ) : null}
        <div className={['cmp-row', invalid.subject ? 'invalid' : '', invalid.subject && shake % 2 === 1 ? 'shake' : ''].filter(Boolean).join(' ')}>
          <label className="rl" htmlFor={`${titleId}-subj`}>
            Subject
          </label>
          <span className="rv">
            <input
              ref={subjectRef}
              id={`${titleId}-subj`}
              value={subject}
              maxLength={400}
              autoComplete="off"
              aria-invalid={!!invalid.subject || undefined}
              placeholder="What this email is about"
              onChange={(e) => {
                setSubject(e.target.value);
                clearField('subject');
                touch();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
                  e.preventDefault();
                  editor.current?.focus('afterGreeting');
                }
              }}
            />
          </span>
        </div>
        <TemplateRow
          templateId={templateId}
          onApply={applyTemplate}
          current={() => ({ subject, html: html.current })}
          onSaved={(t) => {
            setTemplateId(t.id);
            touch();
          }}
        />
      </div>

      <div className="cmp-warn" aria-live="polite">
        {serverError ? (
          <Callout tone="danger" role="alert" title="The email did not go out">
            {serverError}
          </Callout>
        ) : null}
        {!mb ? (
          <Callout
            tone="warn"
            title="Connect your own Gmail to send"
            actions={
              <button
                type="button"
                className="btn btn-sm btn-tile"
                onClick={() => {
                  minimiseComposer();
                  openAccount();
                }}
              >
                Open your account
              </button>
            }
          >
            You have no inbox to send from yet.
          </Callout>
        ) : !mb.canSend ? (
          <Callout
            tone="warn"
            title={mb.kind === 'personal' ? 'Connect your own Gmail to send' : `${mb.address} cannot send`}
            actions={
              mb.kind === 'personal' ? (
                <button
                  type="button"
                  className="btn btn-sm btn-tile"
                  onClick={() => {
                    minimiseComposer();
                    openAccount();
                  }}
                >
                  Open your account
                </button>
              ) : null
            }
          >
            {mb.kind === 'personal' ? withoutTitle(mb.problem, 'Connect your own Gmail to send') : mb.problem}
            {otherSender ? ' Pick another inbox in From.' : ''}
          </Callout>
        ) : null}
        {refused.length ? (
          <Callout tone="danger" title={refused.length === 1 ? 'Clientbook will not send to this address' : `Clientbook will not send to ${refused.length} of these addresses`}>
            <ul className="cmp-refused">
              {refused.map((r) => (
                <li key={r.email}>
                  <span className="grow">{problems.get(r.email.toLowerCase())}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      const e = r.email;
                      setTo((l) => l.filter((x) => x.email !== e));
                      setCc((l) => l.filter((x) => x.email !== e));
                      setBcc((l) => l.filter((x) => x.email !== e));
                      clearField('to');
                      touch();
                    }}
                  >
                    Remove {r.name || r.email}
                  </button>
                </li>
              ))}
            </ul>
          </Callout>
        ) : null}
        {attachProblem ? (
          <Callout
            tone="danger"
            title="That file is too big to attach"
            role="alert"
            actions={
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAttachProblem(null)}>
                OK
              </button>
            }
          >
            {attachProblem}
          </Callout>
        ) : null}
        {merged && to.length > 1 ? (
          <Callout tone="info" title="Each person in To gets their own copy">
            {'{first} and {company} are filled in for each of them.'}
            {cc.length + bcc.length ? ' Move the Cc and Bcc people into To, or take out the merge fields.' : ''}
          </Callout>
        ) : null}
      </div>

      <FormatBar ref={fmt} editor={editor} />

      <div className="cmp-body">
        <Editor
          ref={editor}
          initialHtml={init.html}
          onChange={(v) => {
            html.current = v;
            clearField('body');
            touch();
          }}
          onLinkKey={() => fmt.current?.openLink()}
          onPasteFiles={addFiles}
          invalid={!!invalid.body}
        />
        {base.quote ? (
          <div className="cmp-quote">
            <button type="button" className="cmp-quote-t" aria-expanded={quoteOpen} onClick={() => setQuoteOpen((v) => !v)}>
              <Icon name={quoteOpen ? 'chev-down' : 'chev-right'} className="i-xs" />
              {base.mode === 'forward' ? 'Forwarded message' : quoteOpen ? 'Hide the earlier message' : 'Show the earlier message'}
            </button>
            {quoteOpen ? <div className="cmp-quote-b" dangerouslySetInnerHTML={{ __html: cleanPastedHtml(base.quote.html) }} /> : null}
          </div>
        ) : null}
      </div>

      <AttachmentList
        items={att.items}
        onRemove={(k) => {
          att.remove(k);
          clearField('attachments');
          setAttachProblem(null);
        }}
      />

      <div className="cmp-f">
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          tabIndex={-1}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <button type="button" className={['icon-btn', 'sm', invalid.attachments ? 'bad' : ''].filter(Boolean).join(' ')} aria-label="Attach a file" data-tip="Attach a file · up to 25 MB" onClick={() => fileInput.current?.click()}>
          <Icon name="clip" />
        </button>
        <MergeMenu values={{ first: mergeValues?.first ?? null, company: mergeValues?.company ?? null }} onInsert={(f) => editor.current?.insertText(f)} />
        <Switch
          checked={trackOpens}
          onChange={(v) => {
            setTrackOpens(v);
            touch();
          }}
        >
          Track opens
        </Switch>
        <span className="spacer" />
        {noteEl}
        <button type="button" className="icon-btn sm" aria-label="Discard draft" data-tip="Discard draft" onClick={discard}>
          <Icon name="trash" />
        </button>
        <SendSplit
          sendAt={sendAt}
          busy={send.isPending}
          onSend={submit}
          onSchedule={(at) => {
            setSendAt(at);
            clearField('sendAt');
            touch();
          }}
        />
      </div>

      {dragging ? (
        <div className="cmp-drop" aria-hidden="true">
          <Icon name="clip" />
          <b>Drop files to attach them</b>
          <span>Up to 25 MB in all</span>
        </div>
      ) : null}
    </div>
  );
}

export function FromRow({ mailboxes, value, onChange, invalid }: { mailboxes: ComposeMailbox[]; value: string | null; onChange: (id: string) => void; invalid: boolean }) {
  const pop = usePopover('menu');
  const mb = mailboxes.find((m) => m.id === value) ?? null;
  return (
    <div className={['cmp-row', 'cmp-from', invalid ? 'invalid' : ''].filter(Boolean).join(' ')}>
      <span className="rl" id="cmp-from-l">
        From
      </span>
      <span className="rv">
        {mb ? (
          <>
            <b>{mb.address}</b>
            {mailboxChip(mb)}
          </>
        ) : (
          <span className="muted">No inbox to send from</span>
        )}
        {mailboxes.length > 1 ? (
          <button type="button" className="btn btn-sm btn-ghost" aria-describedby="cmp-from-l" {...pop.anchorProps}>
            Change
            <Icon name="chev-down" />
          </button>
        ) : null}
      </span>
      <Popover state={pop} width={300} maxWidth={380} align="left" label="Send from">
        <MenuHead>Send from</MenuHead>
        {mailboxes.map((m) => (
          <MenuItem
            key={m.id}
            icon={m.kind === 'shared' ? 'users' : 'mail'}
            checked={m.id === value}
            sub={m.canSend ? (m.kind === 'shared' ? 'Shared' : 'Your Gmail') : 'Cannot send yet'}
            onSelect={() => onChange(m.id)}
          >
            {m.address}
          </MenuItem>
        ))}
      </Popover>
    </div>
  );
}
